import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DownloadSimple, IconContext, MoneyWavy, Ticket, CopySimple } from '@phosphor-icons/react';
import { Button, InfoCard, Modal, Table } from 'app/components';
import { getEventDashboard, getRefundDashboard } from '../../api';
import { getBookingsList, getBookingById } from '../../../booking/api';
import CopyToClipboard from 'react-copy-to-clipboard';

const columns = [
  { field: "name", label: "Name" },
  { field: "phone", label: "Phone number" },
  { field: "email", label: "Email" },
  { field: "ticketCount", label: "Ticket count", sortable: true },
  { field: "amount", label: "Amount" },
  { field: "category", label: "Category" },
  { field: "refundStatus", label: "Refund Status" },
  { field: "refundTransaction", label: "Refund Transaction" },
];

const sorts = [{ field: "ticketCount", label: "Ticket Count" }, {field: "amount", label: "Amount"}];
const appliedSorts = [{ field: "ticketCount", order: "desc" }, {field: "amount", order: "desc"}];
const searchText = "";
const pageSize = 6; // 6 rows per page
const selectedPage = 1;
const showSearch = true;


const ReportCard = (props) => {
  const {
    icon,
    label,
    value,
    valueSuffix,
  } = props;

  return (
    <div className="p-6 flex flex-col gap-6 shadow-xs border rounded-lg border-solid border-grey-200">
      {icon && (
        <IconContext.Provider
          value={{
            color: '#FF2459',
            size: 32,
          }}
        >
          <div className="p-6 bg-light w-fit h-fit rounded-lg">
            {icon}
          </div>
        </IconContext.Provider>
      )}
      <div className="flex flex-col gap-3 text-black">
        <div className="heading-16">{label}</div>
        <div className="flex gap-3 items-baseline">
          <div className="heading-36">{value}</div>
          <div className="label-16">{valueSuffix}</div>
        </div>
      </div>
    </div>
  );
};

const RefundDashboard = ({refundInfo, stateName }) => {
  const params = useParams();
  const { eventDetails } = useSelector((state) => state[stateName || 'eventDashboard']);
  const { dashboard } = useSelector((state) => state.eventDashboard);
  const [ modal, setModal ] = useState(false);
  const [refundData, setRefundData] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [eventBookings, setEventBookings] = useState([]);

  function isPastDate(dateArray, bookingDate) {
    if (!Array.isArray(dateArray) || !bookingDate) {
      return false;
    }
    const [year, month, day, hour, minute, second] = dateArray;
    const baseDate = new Date(year, month - 1, day, hour, minute, second);
    // Parse bookingDate string "YYYY-MM-DD HH:mm:ss"
    const bookingDateObj = new Date(bookingDate.replace(' ', 'T'));
    return bookingDateObj < baseDate;
  }

  useEffect(() => {
    if (eventDetails?.id) {
      getBookingsList(eventDetails?.id, (data) => {
        setEventBookings(data); 
      });
    }
  }, [eventDetails?.id]);

  // Filter bookings to only include those for the current event
  const filteredEventBookings = useMemo(() => {
    return eventBookings?.filter(booking => booking?.event_details?.id === eventDetails?.id) || [];
  }, [eventBookings, eventDetails?.id]);

  const handleCopy = (id, text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const onListingClick = (booking) => {
    getBookingById(booking?.id, (data) => {
      setBookingData(data);
    });
  };

  // Calculate total pages based on number of rows
  const totalPages = useMemo(() => {
    const rowCount = refundInfo?.length || 0;
    return Math.ceil(rowCount / pageSize);
  }, [refundInfo]);

  // Calculate total tickets sold using the same logic as Dashboard
  const ticketsDetails = useMemo(() => {
    if (dashboard?.tickets_category?.length) {
      const config = {};
      dashboard?.tickets_category?.forEach((ticket) => {
        config[ticket?.ticket_id] = ticket;
      });
      return config;
    }
    return {};
  }, [dashboard?.tickets_category]);


  const totalTicketsSold = useMemo(() => {
    if (!eventDetails?.reschedule_time) return 0;
    return filteredEventBookings?.reduce((total, booking) => {
      if (isPastDate(eventDetails?.reschedule_time, booking?.booking_date)) {
        return total + (booking?.tickets_booked || 0);
      }
      return total;
    }, 0) || 0;
  }, [filteredEventBookings, eventDetails?.reschedule_time]);

  // Compute refund tallies
  const refundDetails = refundData?.refund_details || [];
  const pending = refundInfo?.filter(r => r?.refund_properties?.status !== 'succeeded').length || 0;
  const completed = refundInfo?.filter(r => r?.refund_properties?.status === 'succeeded').length || 0;
  const optedForRefund = refundInfo?.length || 0;


  // Prepare table rows
  const rows = refundInfo?.map((r, index) => ({
    name: r?.details?.details[0]?.full_name,
    phone: r?.details?.details[0]?.phone_number,
    email: r?.details?.details[0]?.email,
    ticketCount: r?.item_count,
    amount: r?.amount,
    category: r?.category,
    refundStatus: r?.refund_properties?.status,
    refundTransaction: (
      <div className="flex items-center gap-2">
        <span className = "whitespace-nowrap">{r?.refund_properties?.network_reference_number}</span>
        <div className="flex items-center gap-1">
          {copiedId === index && <span className="text-[#FF2459] text-sm whitespace-nowrap">Copied!</span>}
          <CopyToClipboard 
            onCopy={() => handleCopy(index, r?.refund_properties?.network_reference_number)} 
            text={r?.refund_properties?.network_reference_number}
          >
            <CopySimple
              size={20}
              color={copiedId === index ? "#FF2459" : "black"}
              className="cursor-pointer"
            />
          </CopyToClipboard>
        </div>
      </div>
    ),
  }));

  return (
    window.innerWidth <= 768 ? (
    <div className="px-4 py-4 flex-1">
      <div className="heading-24 pb-6">Refund Management</div>
      {!eventDetails.status == 'CANCELLED' ?
      (<div className="grid grid-cols-1 gap-6">
        <ReportCard
          label="Users who have opted for refund"
          value={`${optedForRefund}`}
          icon={<MoneyWavy />}
        />
        <ReportCard
          label="Total tickets"
          value={`${totalTicketsSold}`}
          icon={<Ticket />}
        />
      </div>) : (
        <div className="grid grid-cols-1 gap-6">
        <ReportCard
          label="Total tickets to be refunded"
          value={`${totalTicketsSold}`}
          icon={<Ticket/>} 
        />
      </div>
      )}
      <InfoCard 
        className="my-6 bg-mint" 
        content={
          <div className="flex flex-col gap-4">
            <span>Refund has been processed successfully!</span>
            <Button type="primary-black" onClick={() => setModal(!modal)}>View Content</Button>
          </div>
        }
      />
      <div className="horizontal-divider my-6" />
      <div className="flex flex-col gap-6">
        <div className="heading-16">Refund Status</div>
        <div className="grid grid-cols-1 gap-6">
          <ReportCard
            label="Pending"
            value={pending}
            valueSuffix={`out of ${optedForRefund}`}
          />
          <ReportCard 
            label = "Completed"
            value={completed}
            valueSuffix={`out of ${optedForRefund}`} /> 
        </div>
      </div>
      {modal && (
        <Modal size = "extra-large" className = "px-4" hasClose = {true} onClose = {() => setModal(!modal)}>
        <div className='px-4 py-4 flex-1'>
        {refundInfo?.length ? (
          <div className="flex flex-col gap-3">
            {refundInfo?.map((item, index) => {
              const amount = item?.amount;
              const category = item?.category;
              const refundStatus = item?.refund_properties?.status;
              const name = item?.details?.details?.[0]?.full_name || '—';
              const phone = item?.details?.details?.[0]?.phone_number || '—';
              const email = item?.details?.details?.[0]?.email || '—';
              const refundTransaction = item?.refund_properties?.network_reference_number || '—';
              return (
                <div
                  key={index}
                  className="w-full bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm active:scale-[0.98] transition cursor-pointer"
                > 
                  {/* Content */}
                  <div className="flex flex-col flex-1">
                    <div className="font-semibold text-[15px] text-gray-900">
                      {name}
                    </div>

                    {/* Badge */}
                    <div className="mt-1">
                      {refundStatus !== 'succeeded' ? (
                        <span className="text-[13px] px-2 py-[2px] rounded-full bg-gray-100 text-gray-500">
                          Refund Pending
                        </span>
                      ) : (
                        <span className="text-[13px] px-2 py-[2px] rounded-full bg-green-100 text-green-700">
                          Refund Succeeded
                        </span>
                      )}
                    </div>

                    {/* Tier + Count */}
                    <div className="flex items-center gap-2 text-[14px] text-gray-700 mt-1">
                      <span>₹{amount}</span>
                      <span>•</span>
                      <span>{category}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[14px] text-gray-700 mt-1">
                      <span>📞 {phone}</span>
                      <span>•</span>
                      <span>{email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[14px] text-gray-700 mt-1">
                      <span><strong>Transaction ID:</strong></span> <span>{refundTransaction}</span>  
                       <div className="flex items-center gap-1">
                        {copiedId === index && <span className="text-[#FF2459] text-sm whitespace-nowrap">Copied!</span>}
                        <CopyToClipboard 
                          onCopy={() => handleCopy(index, refundTransaction)} 
                          text={refundTransaction}
                        >
                          <CopySimple
                            size={20}
                            color={copiedId === index ? "#FF2459" : "black"}
                            className="cursor-pointer"
                          />
                        </CopyToClipboard>
                      </div>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-16">No tickets refunded yet!</div>
        )}
      </div>
        </Modal>
      )}
    </div>
    ) : (
    <div className="px-14 py-9 flex-1">
      <div className="flex justify-between pb-9">
        <div className="heading-24">Refund Management</div>
      </div>
      {!eventDetails.status == 'CANCELLED' ?
      (<div className="grid grid-cols-2 gap-9">
        <ReportCard
          label="Users who have opted for refund"
          value={`${optedForRefund}`}
          icon={<MoneyWavy />}
        />
        <ReportCard
          label="Total tickets"
          value={`${totalTicketsSold}`}
          icon={<Ticket />}
        />
      </div>) : (
        <div className="grid grid-cols-2 gap-9">
        <ReportCard
          label="Total tickets to be refunded"
          value={`${totalTicketsSold}`}
          icon={<Ticket/>} 
        />
      </div>
      )}
      <InfoCard 
        className="my-9 bg-mint" 
        content={
          <div className="flex justify-end items-center gap-4">
            <span>Refund has been processed successfully!</span>
            <Button type="primary-black" onClick={() => setModal(!modal)}>View Content</Button>
          </div>
        }
      />
      <div className="horizontal-divider my-9" />
      <div className="flex flex-col gap-6">
        <div className="heading-16">Refund Status</div>
        <div className="grid grid-cols-3 gap-6">
          <ReportCard
            label="Pending"
            value={pending}
            valueSuffix={`out of ${optedForRefund}`}
          />
          <ReportCard 
            label = "Completed"
            value={completed}
            valueSuffix={`out of ${optedForRefund}`} /> 
        </div>
      </div>
      {modal && (
        <Modal size = "extra-large" className = "px-8" hasClose = {true} onClose = {() => setModal(!modal)}>
          <div className="overflow-x-auto px-8">
            <Table 
              rows={rows}
              columns={columns}
              sorts={sorts}
              appliedSorts={appliedSorts}
              totalPages={totalPages}
              selectedPage={selectedPage}
              pageSize={pageSize}
              showSearch={showSearch}
              hasPagination={true}
              className="w-full min-w-[800px]" />
          </div>
        </Modal>
      )}
    </div>
  ));
};


export default RefundDashboard;

